import { FunctionComponent } from "react";
import { RefSource } from "../types";
import { SOURCE_DOT_COLORS } from "../utils";

/**
 * Hover-only panel listing the sources that contribute to an ERD ref
 * edge, filtered to the user's enabled sources. Each pill renders the
 * source name in white on the source's signature color and doubles as
 * the canvas legend — same colors are echoed next to source labels in
 * the Relationships popover so users learn the mapping without an
 * external reference.
 *
 * Sized in user space (no zoom-scaling) so it stays legible. Mounted by
 * `RefEdge` only when `hovered` is true and at least one enabled source
 * contributes.
 */
export const CorroborationPanel: FunctionComponent<{
  x: number;
  y: number;
  sources: RefSource[];
}> = ({ x, y, sources }) => {
  const PILL_HEIGHT = 16;
  const PILL_GAP = 4;
  const PAD_X = 9;
  const FONT_SIZE = 10;
  // ~6.2 px/char at fontSize=10 weight=600. Wide enough for the longest
  // source name ("contract"/"semantic"/"inferred") without clipping.
  const widths = sources.map(s => Math.max(44, s.length * 6.2 + PAD_X * 2));
  const totalWidth =
    widths.reduce((a, b) => a + b, 0) + (sources.length - 1) * PILL_GAP;
  let cursor = -totalWidth / 2;
  return (
    <g transform={`translate(${x}, ${y - PILL_HEIGHT / 2})`} pointerEvents="none">
      {sources.map((src, i) => {
        const w = widths[i];
        const xPos = cursor;
        cursor += w + PILL_GAP;
        return (
          <g key={src} transform={`translate(${xPos}, 0)`}>
            <rect
              width={w}
              height={PILL_HEIGHT}
              rx={PILL_HEIGHT / 2}
              fill={SOURCE_DOT_COLORS[src]}
            />
            <text
              x={w / 2}
              // Hard-coded baseline from font metrics — more reliable
              // than `dominantBaseline` (Chromium engines interpret it
              // differently). Visual center of caps-and-x-height letters
              // is at box_center + ~0.35 × font_size below the y midline.
              y={PILL_HEIGHT / 2 + FONT_SIZE * 0.35}
              textAnchor="middle"
              fill="white"
              fontSize={FONT_SIZE}
              fontWeight="600"
              fontFamily="system-ui, sans-serif"
              style={{ userSelect: "none" }}
            >
              {src}
            </text>
          </g>
        );
      })}
    </g>
  );
};
